import type { Account } from '@/common/types'
import { create } from 'zustand'
import { reportError } from '@/common/errorHandling/sentryUtils'
import { resolveActiveAccount } from './accountSelectionResolver'

const STORAGE_KEY = 'six-active-account'

// How long a locally-confirmed terms acceptance is trusted over the accounts API response.
// The backend propagates acceptance asynchronously (event-driven), so a refetch shortly after
// accepting can still return termsAccepted:false. After this window, server truth always wins.
const TERMS_ACCEPTED_GRACE_PERIOD_MS = 24 * 60 * 60 * 1000 // 1 day

// Not part of the store's reactive state: only setAccounts and confirmTermsAccepted read/write
// this, so it doesn't need to trigger re-renders.
const termsAcceptedGraceExpiryByAccountId = new Map<string, number>()

type AccountState = {
    accountsById: Record<string, Account>
    primaryAccountId: string | null
    activeAccountId: string | null
    isAccountSwitching: boolean
    accountSwitchTargetId: string | null
    switchSessionToken: number
    isAccountsLoaded: boolean
}

type AccountStore = AccountState & {
    getActiveAccount: () => Account | null
    getAccounts: () => Account[]
    getPrimaryAccount: () => Account | null
    setPrimaryAccountId: (id: string | null) => void
    setAccounts: (accounts: Account[], userAccountId?: string | null) => void
    setActiveAccountId: (id: string | null) => void
    setAccountsLoaded: (loaded: boolean) => void
    confirmAccountStatus: (accountId: string) => void
    confirmTermsAccepted: (accountId: string) => void
    startAccountSwitch: (targetId: string | null) => void
    finishAccountSwitch: () => void
    clearAll: () => void
}

const getInitialActiveAccountId = () =>
    typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) || null : null

const persistActiveAccountId = (id: string | null) => {
    if (typeof window === 'undefined') return
    if (id) {
        localStorage.setItem(STORAGE_KEY, id)
    } else {
        localStorage.removeItem(STORAGE_KEY)
    }
}

/**
 * Checks if an account is owned by the user (not linked/shared).
 *
 * Business Rule: A user can have multiple accounts but only ONE owned INDIVIDUAL account.
 * Linked/shared INDIVIDUAL accounts are accessible but not owned. Only owned accounts
 * can be set as the primary account.
 *
 * Owned accounts are identified by userRole === 'OWNER'.
 */
const isOwnedAccount = (account: Account): boolean => account.userRole === 'OWNER'

const getAccountById = (accountsById: Record<string, Account>, accountId: string | null): Account | null =>
    accountId ? (accountsById[accountId] ?? null) : null

const initialState: AccountState = {
    accountsById: {},
    primaryAccountId: null,
    activeAccountId: getInitialActiveAccountId(),
    isAccountSwitching: false,
    accountSwitchTargetId: null,
    switchSessionToken: 0,
    isAccountsLoaded: false,
}

const useAccountStore = create<AccountStore>((set, get) => ({
    ...initialState,

    getActiveAccount: () => {
        const { accountsById, activeAccountId } = get()
        return getAccountById(accountsById, activeAccountId)
    },

    getAccounts: () => {
        const { accountsById } = get()
        return Object.values(accountsById)
    },

    getPrimaryAccount: () => {
        const { accountsById, primaryAccountId } = get()
        return getAccountById(accountsById, primaryAccountId)
    },

    /**
     * Update accounts and sync primary account from API.
     *
     * Atomically updates both accountsById and primaryAccountId.
     * Uses the API's accountCategory to determine which account is primary.
     */
    setAccounts: (accounts, userAccountId = null) => {
        const state = get()
        const now = Date.now()

        const accountsById = accounts.reduce(
            (acc, account) => {
                const graceExpiry = termsAcceptedGraceExpiryByAccountId.get(account.accountId)
                const withinGracePeriod = graceExpiry !== undefined && now < graceExpiry
                const termsAccepted = withinGracePeriod ? true : account.termsAccepted
                return {
                    ...acc,
                    [account.accountId]: { ...account, termsAccepted },
                }
            },
            {} as Record<string, Account>,
        )

        // Determine primary: use API's accountCategory === 'PRIMARY' if available
        // Fallback to current if no API-marked primary is found
        const apiPrimary = accounts.find((a) => a.accountCategory === 'PRIMARY')
        const nextPrimary = apiPrimary?.accountId ?? state.primaryAccountId ?? null

        // Atomically resolve next active account if current is no longer available.
        // This maintains the invariant: if activeAccountId is set, it exists in accountsById.
        const nextActiveId = resolveActiveAccount({
            currentActive: state.activeAccountId,
            userAccountId,
            primaryAccount: nextPrimary ? accountsById[nextPrimary] : null,
            accounts,
            failedAccountIds: new Set(),
        })

        set({
            accountsById,
            primaryAccountId: nextPrimary,
            activeAccountId: nextActiveId,
            isAccountsLoaded: true,
        })
    },

    setPrimaryAccountId: (id) => set({ primaryAccountId: id }),

    setAccountsLoaded: (loaded) => set({ isAccountsLoaded: loaded }),

    setActiveAccountId: (id) => {
        persistActiveAccountId(id)
        set({ activeAccountId: id ?? null })
    },

    confirmAccountStatus: (accountId) =>
        set((state) => {
            const existing = state.accountsById[accountId]
            if (!existing) {
                const error = new Error(
                    `confirmAccountStatus called for non-existent account: ${accountId}. This is a developer error—confirm should only be called on accounts in the store.`,
                )
                reportError(error, {
                    tags: { source: 'accountStore', function: 'confirmAccountStatus' },
                    extra: { accountId },
                })
                return state
            }

            return {
                accountsById: {
                    ...state.accountsById,
                    [accountId]: {
                        ...existing,
                        confirmationStatus: 'CONFIRMED' as const,
                    },
                },
            }
        }),

    confirmTermsAccepted: (accountId) => {
        termsAcceptedGraceExpiryByAccountId.set(accountId, Date.now() + TERMS_ACCEPTED_GRACE_PERIOD_MS)

        set((state) => {
            const existing = state.accountsById[accountId]
            if (!existing) return state

            return {
                accountsById: {
                    ...state.accountsById,
                    [accountId]: { ...existing, termsAccepted: true },
                },
            }
        })
    },

    startAccountSwitch: (targetId) =>
        set((state) => ({
            isAccountSwitching: true,
            accountSwitchTargetId: targetId,
            switchSessionToken: state.switchSessionToken + 1,
        })),

    finishAccountSwitch: () =>
        set(() => ({
            isAccountSwitching: false,
            accountSwitchTargetId: null,
        })),

    clearAll: () => {
        persistActiveAccountId(null)
        termsAcceptedGraceExpiryByAccountId.clear()
        set(() => ({ ...initialState, activeAccountId: null }))
    },
}))

export type { AccountState }
export { isOwnedAccount }
export default useAccountStore
